import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ListingItem } from 'depoto-core/src/models'
import { CoreService } from '../../services'
import { Category, Checkout, Currency, File, Product, ProductMove } from 'depoto-core/src/entities'
import { suppliersColumns } from '../../utils/definitions.util'
import { SchemaUtil, wait, getThumbUrl as imageGetThumbUrl } from '../../utils'
import * as moment from 'moment'
import { TranslateService } from '@ngx-translate/core'
import { ModalProductSearchStatsComponent } from '../../components/modal-components/modal-product-search-stats/modal-product-search-stats'

const DEFAULT_FILTERS = {
  visible: false,
  expanded: false,
  checkouts: [],
  products: [],
  categories: [],
  dateFrom: moment().clone().add(-2, 'months').startOf('month').format('YYYY-MM-DD'),
  dateTo: null,
  sortProp: 'name',
  sortDir: 'asc',
}

type ProductListItem = {
  fullName: string
  batch: string
  quantitySum: number
  valueSum: number
  product: Product
  productMove: ProductMove
}

@Component({
  selector: 'app-stats',
  templateUrl: 'stats.component.html',
  styleUrls: ['stats.component.scss'],
})
export class StatsComponent implements OnInit, OnDestroy {
  @ViewChild(ModalProductSearchStatsComponent) productSearchModal: ModalProductSearchStatsComponent
  listingColumns: ListingItem[] = suppliersColumns
  productMoves: ProductMove[] = []
  checkouts: Partial<Checkout>[] = []
  // products: Product[] = [] // helper. todo remove?
  productListItems: ProductListItem[] = []
  totalItems = 0
  endPage: number
  currentPageProgress: number
  filters = { ...DEFAULT_FILTERS }
  isPricesWithVat: boolean = true
  canCreate = false
  loading = false
  purchaseCurrencyFromCompany: Currency // there is no way to change this across hs, so it's certain we can use this
  valueSum: number = 0
  batchCount: number = 0
  chartTimeOptions = ['stats.chart.month', 'stats.chart.day']
  chartTime: 'stats.chart.month' | 'stats.chart.day' = 'stats.chart.month'
  chartValueOptions = ['stats.chart.value', 'stats.chart.amount']
  chartValue: 'stats.chart.value' | 'stats.chart.amount' = 'stats.chart.value'
  chartIsMergingCheckouts = false
  isExpandedByBatches = false
  chartData: any[] = []
  tableSortProp: string = 'fullName'
  tableSortDir: 'asc' | 'desc' = 'asc'
  isAborted = false

  constructor(
    protected core: CoreService,
    private readonly translateService: TranslateService,
  ) {
    // this.getStorageKey('stats')
  }

  async ngOnInit() {
    // this.filters = this.loadViewSettings('stats') || DEFAULT_FILTERS
    this.isAborted = false
    this.filters = { ...DEFAULT_FILTERS }
    if (this.core.services.user.user) {
      this.checkPerms()
      this.purchaseCurrencyFromCompany = this.core.services.user.user.company.currency
    }
    this.core.services.user.currentUserEmitter.subscribe().then(() => {
      this.checkPerms()
      if (this.core.services.user.user) {
        this.purchaseCurrencyFromCompany = this.core.services.user.user.company.currency
      }
    })
    this.checkouts = [{ id: 0, name: 'stats.deductions' }, ...this.core.lists.checkouts]
    await this.loadFilters()
    await this.filter()
  }

  ngOnDestroy() {
    this.abortAllRequests()
  }

  async abortAllRequests() {
    // this.core.services.http.abortAllRequests()
    this.isAborted = true
    if (this.core.services.http.requestQueue?.length > 0) {
      this.core.services.http.requestQueue.forEach((r, i) => {
        r.isCancelled = true
        r.abortCtrl.abort()
      })
    }
    this.currentPageProgress = 0
    await wait(250)
    this.isAborted = false
  }

  checkPerms() {
    // todo!!!
    this.canCreate =
      this.core.services.user.hasRole('CLIENT_MENU_SOLDITEMS') || this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
  }

  toggleProductInFilters(prod: Product) {
    if (this.filters.products?.find(p => p.id === prod.id)) {
      this.filters.products = this.filters.products.filter(p => p.id !== prod.id)
    } else {
      this.filters.products.push(prod)
    }
  }

  get queryFilters() {
    const f: any = {
      type: 'out',
      checkouts: this.filters.checkouts?.filter(c => c > 0).length
        ? this.filters.checkouts.filter(c => c > 0)
        : undefined,
      products: this.filters.products?.length ? this.filters.products.map(p => p.id) : undefined,
    }
    if (this.filters?.checkouts?.filter(c => c === 0).length) {
      f.withoutCheckout = true
    }
    if (this.filters?.categories?.length) {
      f.categories = this.filters.categories.map(c => c.id)
    }

    Object.keys(f).forEach(k => {
      if (f[k] === undefined) {
        delete f[k]
      }
    })
    if (this.filters.dateFrom?.length > 0 || this.filters.dateTo?.length > 0) {
      f.created = {
        ...(this.filters.dateFrom?.length && {
          left_date: this.filters.dateFrom,
        }),
        ...(this.filters.dateTo?.length && {
          right_date: this.filters.dateTo + 'T23:59:59',
        }),
      }
    }
    return f
  }

  async clearFilters() {
    this.filters = { ...DEFAULT_FILTERS }
    this.filters.products = []
    this.isPricesWithVat = true
    this.productSearchModal.searchValue = ''
    await this.abortAllRequests()
    await wait()
    this.loading = false
    await this.filter()
  }

  async filter() {
    return this.filterSerialParallel()
  }

  // todo leave for now - test serial fetching.. (nope, much slower than parallel one)
  async filterSerial() {
    const reqParams = {
      page: 1,
      sort: 'id',
      direction: 'DESC',
      filters: { ...this.queryFilters },
    }
    this.saveFilters()
    this.loading = true
    const result = await this.core.services.productMove.getList(reqParams, SchemaUtil.productMoveForStats)
    this.totalItems = result?.paginator ? result.paginator.totalCount : 0
    this.endPage = result?.paginator ? result.paginator.endPage : 0
    const pMoves: ProductMove[] = []
    this.productMoves = []
    for (let i = 1; i <= this.endPage; i++) {
      // console.log(i)
      const res = await this.core.services.productMove.getList(
        { ...reqParams, page: i },
        SchemaUtil.productMoveForStats,
      )
      res?.items?.forEach(pm => pMoves.push(new ProductMove(pm)))
    }
    this.productMoves = [...pMoves]
    this.loading = false
    this.processData()
  }

  // serial / parallel combo
  async filterSerialParallel() {
    if (this.loading) {
      return
    }
    function* chunkGen(collection: number[], size = 2, i = 0) {
      for (; i < collection.length; i += size) {
        yield collection.slice(i, i + size)
      }
    }
    function chunk(collection: number[], size = 1) {
      const chunked = []
      const gen = chunkGen(collection, size)
      let c = gen.next()
      while (!c.done) {
        chunked.push(c.value)
        c = gen.next()
      }
      return chunked
    }

    const reqParams = {
      page: 1,
      sort: 'id',
      direction: 'DESC',
      filters: { ...this.queryFilters },
    }
    this.saveFilters()
    this.loading = true
    const result = await this.core.services.productMove.getList(reqParams, { id: null })
    this.totalItems = result?.paginator ? result.paginator.totalCount : 0
    this.endPage = result?.paginator ? result.paginator.endPage : 0
    const pages = []
    for (let i = 1; i <= this.endPage; i++) {
      pages.push(i)
    }
    const pMoves: ProductMove[] = []
    this.productMoves = []
    const batches = chunk(pages, 10)
    // console.log({batches})
    for (const batch of batches) {
      if (this.isAborted) {
        return
      }
      await Promise.all(
        batch.map(async i => {
          if (this.isAborted) {
            return
          }
          // console.log(i)
          const res = await this.core.services.productMove.getList(
            { ...reqParams, page: i },
            SchemaUtil.productMoveForStats,
          )
          res?.items?.forEach(pm => pMoves.push(new ProductMove(pm)))
          this.currentPageProgress = i > this.currentPageProgress ? i : this.currentPageProgress
          // console.log(`done ${i}`)
        }),
      )
    }
    this.productMoves = [...pMoves]
    this.loading = false
    this.currentPageProgress = 0
    this.processData()
  }

  // parallel
  async filterParallel() {
    const reqParams = {
      page: 1,
      sort: 'id',
      direction: 'DESC',
      filters: { ...this.queryFilters },
    }
    this.saveFilters()
    this.loading = true
    const result = await this.core.services.productMove.getList(reqParams, { id: null })
    this.totalItems = result?.paginator ? result.paginator.totalCount : 0
    this.endPage = result?.paginator ? result.paginator.endPage : 0
    const promises = []
    for (let i = 1; i <= this.endPage; i++) {
      promises.push(this.core.services.productMove.getList({ ...reqParams, page: i }, SchemaUtil.productMoveForStats))
    }
    const pMoves: ProductMove[] = []
    this.productMoves = []
    Promise.all(promises)
      .then(responses => {
        responses.forEach(response => {
          response?.items?.forEach(pm => {
            pMoves.push(new ProductMove(pm))
          })
        })
      })
      .finally(() => {
        this.productMoves = [...pMoves]
        this.loading = false
        this.processData()
      })
  }

  processData() {
    // const products = this.productMoves.map(pm => pm.product)
    const priceProp = this.isPricesWithVat ? 'purchasePriceAllWithVat' : 'purchasePriceAll'
    this.valueSum = this.productMoves.map(pm => pm[priceProp]).reduce((a, b) => a + b, 0)
    const batches = this.productMoves.map(pm => pm.productDepotFrom?.batch || pm.productDepotTo?.batch)
    const uniqueBatches = Array.from(new Set(batches)).filter(b => !!b)
    this.batchCount = uniqueBatches.length
    // console.log(uniqueBatches.filter(b => !!b))
    const separateProductListItems = this.productMoves.map(pm => ({
      fullName: pm.product.fullName,
      batch: pm.productDepotFrom?.batch || pm.productDepotTo?.batch,
      quantitySum: pm.amount,
      valueSum: Number(pm[priceProp]?.toFixed(2)),
      product: pm.product,
      productMove: pm,
    }))
    if (this.isExpandedByBatches) {
      // todo process productListItems in separate method.. also, simplify this shit!
      this.productListItems = []
      separateProductListItems.forEach(spli => {
        let pliIndex = 0
        if (
          this.productListItems.find((pli, index) => {
            const pliBatch = Array.from(new Set(pli.batch?.split(';')?.filter(b => b !== 'null')))?.[0]
            const found = pli.product.id === spli.product.id && pliBatch == spli.batch
            if (found) {
              pliIndex = index
            }
            return found
          })
        ) {
          this.productListItems[pliIndex].quantitySum = Number(
            (this.productListItems[pliIndex].quantitySum + spli.quantitySum).toFixed(2),
          )
          this.productListItems[pliIndex].valueSum = Number(
            (this.productListItems[pliIndex].valueSum + spli.valueSum).toFixed(2),
          )
          this.productListItems[pliIndex].batch = `${this.productListItems[pliIndex].batch};${spli.batch}`
        } else {
          this.productListItems.push(spli)
        }
      })
    } else {
      this.productListItems = []
      separateProductListItems.forEach(spli => {
        let pliIndex = 0
        if (
          this.productListItems.find((pli, index) => {
            const found = pli.product.id === spli.product.id
            if (found) {
              pliIndex = index
            }
            return found
          })
        ) {
          this.productListItems[pliIndex].quantitySum = Number(
            (this.productListItems[pliIndex].quantitySum + spli.quantitySum).toFixed(2),
          )
          this.productListItems[pliIndex].valueSum = Number(
            (this.productListItems[pliIndex].valueSum + spli.valueSum).toFixed(2),
          )
          this.productListItems[pliIndex].batch = `${this.productListItems[pliIndex].batch};${spli.batch}`
        } else {
          this.productListItems.push(spli)
        }
      })
    }
    this.productListItems = this.productListItems.map(pli => {
      const batch = Array.from(new Set(pli.batch?.split(';')))
        .map(b => (b?.length && b !== 'null' && b !== 'undefined' ? b : 'empty'))
        .join(';')
      return {
        ...pli,
        batch: batch.includes('empty')
          ? ''
          : batch?.split(';')?.length > 1
            ? this.translateService.instant('stats.multiple.batches')
            : batch,
      }
    })
    this.sortTable()
    this.chartData = [...this.productMoves]
  }

  sortTable(sortProp?: string) {
    if (sortProp?.length) {
      if (sortProp === this.tableSortProp) {
        this.tableSortDir === 'asc' ? (this.tableSortDir = 'desc') : (this.tableSortDir = 'asc')
      } else {
        this.tableSortProp = sortProp
        this.tableSortDir = 'asc'
      }
    }
    if (this.tableSortProp === 'batch' && !this.isExpandedByBatches) {
      this.tableSortProp = 'fullName'
      this.tableSortDir = 'asc'
    }
    this.productListItems = [...this.productListItems].sort((a, b) => {
      if (this.tableSortDir === 'asc') {
        return a[this.tableSortProp] > b[this.tableSortProp] ? 1 : -1
      } else {
        return a[this.tableSortProp] > b[this.tableSortProp] ? -1 : 1
      }
    })
  }

  setChartOptions(
    type: 'chart.time' | 'chart.value',
    value: 'stats.chart.month' | 'stats.chart.day' | 'stats.chart.value' | 'stats.chart.amount' | any,
  ) {
    if (type === 'chart.time') {
      this.chartTime = value
    } else if (type === 'chart.value') {
      this.chartValue = value
    }
  }

  getThumbUrl(image: File, format: 'w135wp' | 'w2048wp' = 'w135wp'): string {
    return imageGetThumbUrl(image, format)
  }

  filterCategoriesChanged(categories: Category[]) {
    this.filters.categories = categories
    this.filter()
  }

  onFilterFormKeyPress(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      this.filter()
    }
  }

  saveFilters() {
    const filters = {
      ...this.filters,
      products: this.filters.products.map(p => ({
        id: p.id,
        fullName: p.fullName,
      })),
    }
    this.core.services.storage.set({ key: 'hs_stats', type: 'Object' }, filters)
  }

  async loadFilters() {
    this.filters = (await this.core.services.storage.get({ key: 'hs_stats', type: 'Object' })) || { ...DEFAULT_FILTERS }
  }
}
